import {Injectable} from '@angular/core';
import {NetworkStatusService} from './network-status.service';

export type MediaOfflineType = 'image' | 'video' | 'playlist' | 'unknown';

export interface MediaPrewarmOptions {
  /** Replaces the previous background window owned by this consumer. */
  scope?: string;
  /** Full HLS caching is reserved for media the user actually starts playing. */
  cachePlaylistChildren?: boolean;
  /** User intent may move one shallow task ahead of background viewport work. */
  priority?: boolean;
  /**
   * Keeps a tiny HLS startup path on disk: the selected rendition manifest,
   * optional init/key files and this many media fragments. It never creates a
   * media element or downloads the complete stream.
   */
  startupSegmentCount?: number;
  /**
   * Keep a bounded viewport prewarm available in Cache Storage after its UI
   * owner leaves the screen. Runtime decoders are still always released by
   * their owner; normal cache limits and LRU eviction control disk usage.
   */
  retainCacheOnScopeRelease?: boolean;
}

interface MediaOfflineEntry {
  url: string;
  type: MediaOfflineType;
  size: number;
  cachedAt: number;
  lastAccessedAt: number;
  ttlMs: number;
  source?: 'prewarm' | 'played';
  /** A viewport startup path may outlive its component, but never its cache budget. */
  retainCacheOnScopeRelease?: boolean;
}

interface MediaOfflineIndex {
  version: number;
  entries: Record<string, MediaOfflineEntry>;
}

/** One traversal per root; descendants never hold a network slot while awaiting children. */
interface MediaCacheTraversal {
  root: string;
  type: MediaOfflineType;
  source: 'prewarm' | 'played';
  scopes: Set<string>;
  unscoped: boolean;
  full: boolean;
  startupSegments: number;
  retained: boolean;
  priority: boolean;
  revision: number;
  running: boolean;
  cancelled: boolean;
  processed: Map<string, number>;
}

interface MediaCacheRequest {
  key: string;
  url: string;
  type: MediaOfflineType;
  owners: Set<MediaCacheTraversal>;
  controller?: AbortController;
  running: boolean;
  offline?: boolean;
  resolve: (cached: boolean) => void;
  result: Promise<boolean>;
}

@Injectable({providedIn: 'root'})
export class MediaOfflineService {
  private readonly cacheName = 'wtg-media-offline-v1';
  private readonly indexStorageKey = 'wtg.media.offline.index.v1';
  private readonly indexVersion = 1;
  private readonly imageMaxBytes = 300 * 1024 * 1024;
  private readonly videoMaxBytes = 2 * 1024 * 1024 * 1024;
  private readonly imageTtlMs = 14 * 24 * 60 * 60 * 1000;
  private readonly videoTtlMs = 30 * 24 * 60 * 60 * 1000;
  private readonly playlistTtlMs = 7 * 24 * 60 * 60 * 1000;

  private initialized = false;
  private initialization?: Promise<void>;
  private index: MediaOfflineIndex = {version: this.indexVersion, entries: {}};
  private readonly materializedBlobUrls = new Map<string, string>();
  private readonly materializedBlobUrlAccess = new Map<string, number>();
  private readonly prewarmScopes = new Map<string, Set<string>>();
  private readonly prewarmPlaylistChildren = new Map<string, Set<string>>();
  private readonly playedRoots = new Map<string, number>();
  private readonly playlistChildren = new Map<string, Set<string>>();
  private readonly maxPlayedRoots = 20;
  private readonly maxMaterializedBlobUrls = 20;
  private readonly traversals = new Map<string, MediaCacheTraversal>();
  private readonly requests = new Map<string, MediaCacheRequest>();
  private readonly backgroundQueue: MediaCacheRequest[] = [];
  private readonly playbackWaiting = new Set<object>();
  private backgroundTasksInFlight = 0;
  private offlineTasksInFlight = 0;
  private readonly maxBackgroundTasks = 4;
  private cancelledRequests = 0;
  private deduplicatedRequests = 0;

  constructor(private readonly networkStatus: NetworkStatusService) {}

  async init(): Promise<void> {
    if (!this.isBrowser() || this.initialized) {
      return;
    }
    if (!this.initialization) {
      this.initialization = (async () => {
        this.index = this.readIndex();
        await this.cleanupExpired();
        this.initialized = true;
      })();
    }
    await this.initialization;
  }

  async resolveUrl(url: string, typeHint: MediaOfflineType = 'unknown'): Promise<string> {
    if (!url || !this.isBrowser()) {
      return url;
    }
    if (!this.networkStatus.isOffline()) {
      // The browser already loads the requested asset online. Background
      // caching is intentionally started only by a bounded prewarm scope or
      // after actual video playback; doing it for every pipe evaluation makes
      // an infinite timeline retain the entire browsing history.
      //
      // Do not await Cache Storage index hydration here. HLS startup and a
      // grid-to-modal handoff call this method on the critical path, while the
      // service worker can satisfy the same URL from its HTTP/Cache Storage
      // cache without an object URL. Waiting for `init()` made a fast tap look
      // cold even when all startup fragments had already been persisted.
      return url;
    }

    await this.init();

    const hit = await this.hasCached(url);
    if (!hit) {
      return url;
    }

    this.touch(url);
    if (typeHint === 'playlist') {
      const playlistUrl = await this.tryCreateOfflinePlaylistUrl(url, new Set<string>());
      return playlistUrl || url;
    }
    const objectUrl = await this.tryCreateObjectUrl(url);
    return objectUrl || url;
  }

  prewarmUrls(
    urls: string[],
    typeHint: MediaOfflineType = 'unknown',
    limit: number = 6,
    options: MediaPrewarmOptions = {}
  ): void {
    if (!urls?.length || !this.isBrowser()) {
      return;
    }
    const unique = Array.from(new Set(urls.filter(Boolean))).slice(0, Math.max(0, limit));
    if (options.scope) {
      this.replacePrewarmScope(options.scope, unique);
    }
    unique.forEach((url) => this.enqueueTraversal(url, typeHint, 'prewarm', options));
  }

  /** All renditions remain available offline, but only real playback arms this work. */
  cacheForOffline(url: string, typeHint: MediaOfflineType = 'unknown'): void {
    if (!url || !this.isBrowser()) {
      return;
    }
    this.touchPlayedRoot(url);
    this.enqueueTraversal(url, typeHint, 'played', {cachePlaylistChildren: true});
  }

  /** An active decoder owns its pause independently of other players and modal sessions. */
  setPlaybackWaiting(owner: object, waiting: boolean): void {
    if (waiting) {
      this.playbackWaiting.add(owner);
      this.requests.forEach((request) => request.controller?.abort());
    } else {
      this.playbackWaiting.delete(owner);
      this.drainBackgroundQueue();
    }
  }

  clearPrewarmScope(scope: string): void {
    const previousUrls = this.prewarmScopes.get(scope);
    if (!previousUrls) {
      return;
    }
    this.prewarmScopes.delete(scope);
    previousUrls.forEach((url) => {
      this.releaseTraversalScope(url, scope);
      this.releasePrewarmEntryIfUnused(url);
    });
  }

  clearBlobUrls(): void {
    this.materializedBlobUrls.forEach((blobUrl) => URL.revokeObjectURL(blobUrl));
    this.materializedBlobUrls.clear();
    this.materializedBlobUrlAccess.clear();
  }

  /** Scalar counters avoid retaining media graphs in DevTools. */
  getDiagnostics() {
    return {
      cachedEntries: Object.keys(this.index.entries).length,
      prewarmScopes: this.prewarmScopes.size,
      queuedTasks: this.backgroundQueue.length,
      inFlightTasks: this.backgroundTasksInFlight,
      offlineTasksInFlight: this.offlineTasksInFlight,
      playedRoots: this.playedRoots.size,
      materializedBlobUrls: this.materializedBlobUrls.size,
      playbackWaiters: this.playbackWaiting.size,
      cancelledRequests: this.cancelledRequests,
      deduplicatedRequests: this.deduplicatedRequests,
      traversals: this.traversals.size,
      activeTraversals: [...this.traversals.values()].filter((traversal) => traversal.running).length,
    };
  }

  private enqueueTraversal(url: string, type: MediaOfflineType, source: 'prewarm' | 'played', options: MediaPrewarmOptions): void {
    let traversal = this.traversals.get(url);
    if (traversal) {
      this.deduplicatedRequests++;
    }
    if (!traversal) {
      traversal = {
        root: url, type, source, scopes: new Set<string>(), unscoped: false,
        full: false, startupSegments: 0, retained: false, priority: false,
        revision: 0, running: false, cancelled: false, processed: new Map<string, number>(),
      };
      this.traversals.set(url, traversal);
    }
    if (options.scope) {
      traversal.scopes.add(options.scope);
    } else if (source === 'prewarm') {
      traversal.unscoped = true;
    }
    const full = traversal.full || !!options.cachePlaylistChildren;
    const segments = Math.max(traversal.startupSegments, Math.floor(options.startupSegmentCount || 0));
    const retained = traversal.retained || !!options.retainCacheOnScopeRelease;
    if (full !== traversal.full || segments !== traversal.startupSegments || retained !== traversal.retained || source === 'played' && traversal.source !== source) {
      traversal.revision++;
      traversal.processed.clear();
    }
    traversal.full = full;
    traversal.startupSegments = segments;
    traversal.retained = retained;
    traversal.priority = traversal.priority || !!options.priority;
    if (source === 'played') {
      traversal.source = source;
      this.requests.forEach((request) => {
        if (request.running && !request.offline && request.owners.has(traversal) && this.isOfflineRequest(request)) {
          // Move a promoted prewarm into the single offline lane before continuing.
          request.controller?.abort();
        }
      });
    }
    void this.runTraversal(traversal);
  }

  private async runTraversal(traversal: MediaCacheTraversal): Promise<void> {
    if (traversal.running || traversal.cancelled) {
      return;
    }
    traversal.running = true;
    try {
      let revision: number;
      do {
        revision = traversal.revision;
        // Promotions are coalesced into one new pass, never one continuation per click.
        await this.prefetch(traversal.root, traversal.type, traversal, new Set<string>());
        if (revision !== traversal.revision) {
          traversal.processed.clear();
        }
      } while (!traversal.cancelled && revision !== traversal.revision);
    } catch {
      // Cache availability must never reject a playback or UI event handler.
      traversal.processed.clear();
    } finally {
      traversal.running = false;
      if (traversal.cancelled && traversal.source === 'prewarm') {
        this.releasePrewarmEntryIfUnused(traversal.root);
      }
      if (traversal.unscoped && !traversal.scopes.size && traversal.source !== 'played' && this.traversals.get(traversal.root) === traversal) {
        this.traversals.delete(traversal.root);
      }
      if (traversal.retained && traversal.source === 'prewarm') {
        this.prewarmPlaylistChildren.delete(traversal.root);
      }
    }
  }

  private replacePrewarmScope(scope: string, urls: string[]): void {
    const nextUrls = new Set(urls);
    const previousUrls = this.prewarmScopes.get(scope) || new Set<string>();
    this.prewarmScopes.set(scope, nextUrls);
    previousUrls.forEach((url) => {
      if (!nextUrls.has(url)) {
        this.releaseTraversalScope(url, scope);
        this.releasePrewarmEntryIfUnused(url);
      }
    });
  }

  private releaseTraversalScope(url: string, scope: string): void {
    const traversal = this.traversals.get(url);
    traversal?.scopes.delete(scope);
    if (traversal && !traversal.scopes.size && !traversal.unscoped && traversal.source !== 'played') {
      this.cancelTraversal(traversal);
    }
  }

  private cancelTraversal(traversal: MediaCacheTraversal): void {
    traversal.cancelled = true;
    if (this.traversals.get(traversal.root) === traversal) {
      this.traversals.delete(traversal.root);
    }
    this.requests.forEach((request) => {
      request.owners.delete(traversal);
      if (!request.owners.size) {
        if (request.running) {
          request.controller?.abort();
        } else {
          this.finishRequest(request, false);
        }
      }
    });
  }

  private finishRequest(request: MediaCacheRequest, cached: boolean): void {
    if (this.requests.get(request.key) === request) {
      this.requests.delete(request.key);
    }
    const index = this.backgroundQueue.indexOf(request);
    if (index >= 0) {
      this.backgroundQueue.splice(index, 1);
    }
    request.resolve(cached);
  }

  private isOfflineRequest(request: MediaCacheRequest): boolean {
    return [...request.owners].every((owner) => owner.source === 'played');
  }

  private drainBackgroundQueue(): void {
    if (this.playbackWaiting.size) {
      return;
    }
    // Every slot represents one response, including body persistence. Graph recursion
    // happens outside this scheduler, so a master never blocks its own children.
    this.backgroundQueue.sort((a, b) => Number(this.isOfflineRequest(a)) - Number(this.isOfflineRequest(b))
      || Number([...b.owners].some((owner) => owner.priority)) - Number([...a.owners].some((owner) => owner.priority)));
    while (this.backgroundTasksInFlight < this.maxBackgroundTasks) {
      const index = this.backgroundQueue.findIndex((request) => !request.running
        && (!this.isOfflineRequest(request) || this.offlineTasksInFlight < 1));
      if (index < 0) {
        return;
      }
      const request = this.backgroundQueue.splice(index, 1)[0];
      request.running = true;
      const offline = this.isOfflineRequest(request);
      request.offline = offline;
      this.backgroundTasksInFlight++;
      if (offline) {
        this.offlineTasksInFlight++;
      }
      void this.fetchAndStore(request).finally(() => {
        request.running = false;
        request.controller = undefined;
        this.backgroundTasksInFlight--;
        if (offline) {
          this.offlineTasksInFlight--;
        }
        this.drainBackgroundQueue();
      });
    }
  }

  private async fetchAndStore(request: MediaCacheRequest): Promise<void> {
    const controller = new AbortController();
    request.controller = controller;
    try {
      await this.init();
      if (!request.owners.size || this.playbackWaiting.size) {
        controller.abort();
      }
      if (controller.signal.aborted) {
        throw new DOMException('Background media paused', 'AbortError');
      }
      const cache = await caches.open(this.cacheName);
      if (!await cache.match(request.url)) {
        const response = await this.fetchForCache(request.url, controller.signal);
        if (!response) {
          this.finishRequest(request, false);
          return;
        }
        const type = this.detectType(request.url, response, request.type);
        const size = await this.estimateResponseSize(response);
        if (controller.signal.aborted) {
          throw new DOMException('Background media paused', 'AbortError');
        }
        await cache.put(request.url, response.clone());
        const played = [...request.owners].some((owner) => owner.source === 'played');
        this.index.entries[request.url] = {
          url: request.url, type, size, cachedAt: Date.now(), lastAccessedAt: Date.now(),
          ttlMs: this.getTtl(type), source: played ? 'played' : 'prewarm',
          retainCacheOnScopeRelease: !played && [...request.owners].some((owner) => owner.retained),
        };
        this.persistIndex();
        await this.evictIfNeeded(type);
      }
      if (!request.owners.size && this.index.entries[request.url]?.source === 'prewarm'
        && !this.index.entries[request.url].retainCacheOnScopeRelease) {
        await this.removeEntry(request.url);
      }
      this.finishRequest(request, true);
    } catch (error) {
      if (controller.signal.aborted) {
        this.cancelledRequests++;
        if (request.owners.size) {
          // The same promise survives the pause; resumption cannot spawn a second graph.
          this.backgroundQueue.push(request);
          return;
        }
      }
      this.finishRequest(request, false);
    }
  }

  private releasePrewarmEntryIfUnused(url: string): void {
    const isStillReferenced = Array.from(this.prewarmScopes.values()).some((urls) => urls.has(url));
    if (!isStillReferenced && this.index.entries[url]?.source === 'prewarm') {
      if (this.index.entries[url].retainCacheOnScopeRelease) {
        // The scope no longer owns any RAM resources. Keep only the disk cache
        // and discard this temporary graph so scrolling cannot retain URLs.
        this.prewarmPlaylistChildren.delete(url);
        return;
      }
      void this.removeEntry(url);
      void this.releasePrewarmPlaylistChildren(url, new Set<string>());
    }
  }

  private async releasePrewarmPlaylistChildren(rootUrl: string, visited: Set<string>): Promise<void> {
    if (visited.has(rootUrl)) {
      return;
    }
    visited.add(rootUrl);
    const children = Array.from(this.prewarmPlaylistChildren.get(rootUrl) || []);
    this.prewarmPlaylistChildren.delete(rootUrl);

    for (const childUrl of children) {
      const isStillWarm = Array.from(this.prewarmPlaylistChildren.values()).some((urls) => urls.has(childUrl));
      const isChildRootReferenced = Array.from(this.prewarmScopes.values()).some((urls) => urls.has(childUrl));
      if (isStillWarm || isChildRootReferenced || this.index.entries[childUrl]?.source !== 'prewarm'
        || this.index.entries[childUrl]?.retainCacheOnScopeRelease) {
        continue;
      }
      await this.removeEntry(childUrl);
      await this.releasePrewarmPlaylistChildren(childUrl, visited);
    }
  }

  private touchPlayedRoot(url: string): void {
    this.playedRoots.delete(url);
    this.playedRoots.set(url, Date.now());
    while (this.playedRoots.size > this.maxPlayedRoots) {
      const oldestUrl = this.playedRoots.keys().next().value as string | undefined;
      if (!oldestUrl) {
        return;
      }
      this.playedRoots.delete(oldestUrl);
      const traversal = this.traversals.get(oldestUrl);
      if (traversal) {
        this.cancelTraversal(traversal);
      }
      void this.releasePlayedRoot(oldestUrl, new Set<string>());
    }
  }

  private async releasePlayedRoot(url: string, visited: Set<string>): Promise<void> {
    if (visited.has(url) || this.playedRoots.has(url)
      || [...this.playlistChildren.entries()].some(([parent, children]) => parent !== url && children.has(url))) {
      return;
    }
    visited.add(url);
    const children = Array.from(this.playlistChildren.get(url) || []);
    this.playlistChildren.delete(url);
    await Promise.all(children.map((childUrl) => this.releasePlayedRoot(childUrl, visited)));
    if (!Array.from(this.playlistChildren.values()).some((urls) => urls.has(url))) {
      await this.removeEntry(url);
    }
  }

  private async prefetch(
    url: string,
    type: MediaOfflineType,
    traversal: MediaCacheTraversal,
    ancestors: Set<string>,
    segmentCount: number = traversal.startupSegments,
  ): Promise<boolean> {
    if (traversal.cancelled) {
      return false;
    }
    if (ancestors.has(url)) {
      return true;
    }
    const full = traversal.full;
    const depth = full ? Infinity : segmentCount;
    if (traversal.processed.has(url) && traversal.processed.get(url)! >= depth) {
      return true;
    }
    await this.init();
    if (traversal.cancelled) {
      return false;
    }
    const cache = await caches.open(this.cacheName);
    if (!await cache.match(url)) {
      if (traversal.cancelled) {
        return false;
      }
      // Cache fills always request the complete resource (an empty Range). Keep
      // the range in the key so partial reads cannot ever alias a complete fill.
      const key = `${url}\nrange:`;
      let request = this.requests.get(key);
      if (!request) {
        let resolve!: (cached: boolean) => void;
        const result = new Promise<boolean>((done) => resolve = done);
        request = {key, url, type, owners: new Set([traversal]), running: false, resolve, result};
        this.requests.set(key, request);
        this.backgroundQueue.push(request);
      } else {
        this.deduplicatedRequests++;
        request.owners.add(traversal);
      }
      this.drainBackgroundQueue();
      if (!await request.result || traversal.cancelled) {
        return false;
      }
    }
    if (traversal.cancelled) {
      return false;
    }
    this.touch(url);
    const entry = this.index.entries[url];
    if (entry) {
      if (traversal.source === 'played') {
        entry.source = 'played';
        entry.retainCacheOnScopeRelease = false;
      } else if (entry.source !== 'played' && traversal.retained) {
        entry.retainCacheOnScopeRelease = true;
      }
      this.persistIndex();
    }
    if (type !== 'playlist' || !full && !segmentCount) {
      traversal.processed.set(url, depth);
      return true;
    }
    const response = await cache.match(url);
    if (!response || response.type === 'opaque') {
      return false;
    }
    const lines = (await response.text()).split('\n').map((line) => line.trim());
    const ancestorsWithCurrent = new Set(ancestors).add(url);
    const variant = this.getInitialHlsVariantUrl(lines, url);
    const mediaUrls = lines.filter((line) => line && !line.startsWith('#'))
      .map((line) => this.resolveHlsUrl(line, url)).filter((value): value is string => !!value);
    const tagUrls = this.getHlsTagUrls(lines, url, '#EXT-X-MAP').concat(this.getHlsTagUrls(lines, url, '#EXT-X-KEY'));
    const children = full ? tagUrls.concat(mediaUrls)
      : variant ? [variant] : tagUrls.concat(mediaUrls.slice(0, segmentCount));
    let complete = true;
    for (const child of new Set(children)) {
      if (traversal.cancelled) {
        return false;
      }
      const graph = traversal.source === 'played' ? this.playlistChildren : this.prewarmPlaylistChildren;
      const parent = traversal.source === 'played' ? url : traversal.root;
      const links = graph.get(parent) || new Set<string>();
      links.add(child);
      graph.set(parent, links);
      const childType = child.toLowerCase().includes('.m3u8') ? 'playlist' : 'video';
      complete = await this.prefetch(child, childType, traversal, ancestorsWithCurrent, segmentCount) && complete;
    }
    if (complete) {
      traversal.processed.set(url, depth);
    }
    return complete;
  }

  /** Returns the first declared master-playlist rendition, matching Hls level 0. */
  private getInitialHlsVariantUrl(lines: string[], playlistUrl: string): string | null {
    for (let index = 0; index < lines.length; index += 1) {
      if (!lines[index].startsWith('#EXT-X-STREAM-INF')) {
        continue;
      }
      for (let candidateIndex = index + 1; candidateIndex < lines.length; candidateIndex += 1) {
        const candidate = lines[candidateIndex];
        if (!candidate) {
          continue;
        }
        if (candidate.startsWith('#')) {
          break;
        }
        const url = this.resolveHlsUrl(candidate, playlistUrl);
        if (url) {
          return url;
        }
        break;
      }
    }
    return null;
  }

  private getHlsTagUrls(lines: string[], playlistUrl: string, tag: string): string[] {
    return lines
      .filter((line) => line.startsWith(tag))
      .map((line) => line.match(/URI="([^"]+)"/)?.[1] || null)
      .map((uri) => uri ? this.resolveHlsUrl(uri, playlistUrl) : null)
      .filter((url): url is string => !!url);
  }

  private resolveHlsUrl(value: string, baseUrl: string): string | null {
    try {
      return new URL(value, baseUrl).toString();
    } catch {
      return null;
    }
  }

  private async fetchForCache(url: string, signal: AbortSignal): Promise<Response | null> {
    try {
      const response = await fetch(url, {
        method: 'GET',
        // Uploaded HLS assets use immutable versioned URLs. `force-cache`
        // avoids a 70–135 ms conditional 304 round trip before a viewport
        // prewarm can populate Cache Storage.
        cache: 'force-cache',
        credentials: 'omit',
        signal,
      });
      if (!response.ok && response.type !== 'opaque') {
        return null;
      }
      return response;
    } catch (error) {
      if (signal.aborted) {
        throw error;
      }
      return null;
    }
  }

  private async hasCached(url: string): Promise<boolean> {
    if (!('caches' in window)) {
      return false;
    }
    const cache = await caches.open(this.cacheName);
    const match = await cache.match(url);
    return !!match;
  }

  private async tryCreateObjectUrl(url: string): Promise<string | null> {
    if (this.materializedBlobUrls.has(url)) {
      this.touchMaterializedBlobUrl(url);
      return this.materializedBlobUrls.get(url) || null;
    }
    try {
      const cache = await caches.open(this.cacheName);
      const response = await cache.match(url);
      if (!response || response.type === 'opaque') {
        return null;
      }
      const blob = await response.blob();
      if (!blob || !blob.size) {
        return null;
      }
      const blobUrl = URL.createObjectURL(blob);
      this.registerMaterializedBlobUrl(url, blobUrl);
      return blobUrl;
    } catch {
      return null;
    }
  }

  private async tryCreateOfflinePlaylistUrl(url: string, visited: Set<string>): Promise<string | null> {
    const cacheKey = `playlist:${url}`;
    if (this.materializedBlobUrls.has(cacheKey)) {
      this.touchMaterializedBlobUrl(cacheKey);
      return this.materializedBlobUrls.get(cacheKey) || null;
    }
    if (visited.has(url)) {
      return null;
    }
    visited.add(url);
    try {
      const cache = await caches.open(this.cacheName);
      const response = await cache.match(url);
      if (!response || response.type === 'opaque') {
        return null;
      }
      const body = await response.text();
      const rewrittenLines: string[] = [];

      for (const line of body.split('\n')) {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith('#')) {
          rewrittenLines.push(line);
          continue;
        }

        let childUrl: string;
        try {
          childUrl = new URL(trimmed, url).toString();
        } catch {
          rewrittenLines.push(line);
          continue;
        }

        const childType: MediaOfflineType = childUrl.toLowerCase().includes('.m3u8') ? 'playlist' : 'video';
        const offlineChild = childType === 'playlist'
          ? await this.tryCreateOfflinePlaylistUrl(childUrl, visited) || childUrl
          : await this.tryCreateObjectUrl(childUrl) || childUrl;

        rewrittenLines.push(offlineChild);
      }

      const blob = new Blob([rewrittenLines.join('\n')], {type: 'application/vnd.apple.mpegurl'});
      const blobUrl = URL.createObjectURL(blob);
      this.registerMaterializedBlobUrl(cacheKey, blobUrl);
      return blobUrl;
    } catch {
      return null;
    } finally {
      visited.delete(url);
    }
  }

  private touch(url: string): void {
    const entry = this.index.entries[url];
    if (!entry) {
      return;
    }
    entry.lastAccessedAt = Date.now();
    this.persistIndex();
  }

  private registerMaterializedBlobUrl(key: string, blobUrl: string): void {
    const previousBlobUrl = this.materializedBlobUrls.get(key);
    if (previousBlobUrl && previousBlobUrl !== blobUrl) {
      URL.revokeObjectURL(previousBlobUrl);
    }
    this.materializedBlobUrls.set(key, blobUrl);
    this.touchMaterializedBlobUrl(key);

    while (this.materializedBlobUrls.size > this.maxMaterializedBlobUrls) {
      const oldestKey = this.materializedBlobUrlAccess.keys().next().value as string | undefined;
      if (!oldestKey) {
        return;
      }
      const oldestBlobUrl = this.materializedBlobUrls.get(oldestKey);
      if (oldestBlobUrl) {
        URL.revokeObjectURL(oldestBlobUrl);
      }
      this.materializedBlobUrls.delete(oldestKey);
      this.materializedBlobUrlAccess.delete(oldestKey);
    }
  }

  private touchMaterializedBlobUrl(key: string): void {
    this.materializedBlobUrlAccess.delete(key);
    this.materializedBlobUrlAccess.set(key, Date.now());
  }

  private async evictIfNeeded(type: MediaOfflineType): Promise<void> {
    const poolTypes = this.toPoolTypes(type);
    for (const poolType of poolTypes) {
      const maxBytes = poolType === 'image' ? this.imageMaxBytes : this.videoMaxBytes;
      const poolEntries = this.getEntriesByPool(poolType);
      let poolSize = poolEntries.reduce((acc, entry) => acc + (entry.size || 0), 0);
      if (poolSize <= maxBytes) {
        continue;
      }

      const sorted = [...poolEntries].sort((a, b) => a.lastAccessedAt - b.lastAccessedAt);
      for (const entry of sorted) {
        await this.removeEntry(entry.url);
        poolSize -= entry.size || 0;
        if (poolSize <= maxBytes) {
          break;
        }
      }
    }
  }

  private async cleanupExpired(): Promise<void> {
    const now = Date.now();
    const candidates = Object.values(this.index.entries).filter((entry) => now - entry.cachedAt > entry.ttlMs);
    for (const entry of candidates) {
      await this.removeEntry(entry.url);
    }
  }

  private async removeEntry(url: string): Promise<void> {
    this.traversals.forEach((traversal) => {
      if (traversal.processed.has(url)) {
        traversal.processed.clear();
      }
    });
    try {
      const cache = await caches.open(this.cacheName);
      await cache.delete(url);
    } catch {
      // Ignore cache delete failures.
    }

    delete this.index.entries[url];
    this.persistIndex();

    const blobUrl = this.materializedBlobUrls.get(url);
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      this.materializedBlobUrls.delete(url);
    }
    const playlistBlobUrl = this.materializedBlobUrls.get(`playlist:${url}`);
    if (playlistBlobUrl) {
      URL.revokeObjectURL(playlistBlobUrl);
      this.materializedBlobUrls.delete(`playlist:${url}`);
    }
  }

  private getEntriesByPool(type: 'image' | 'video'): MediaOfflineEntry[] {
    return Object.values(this.index.entries).filter((entry) => {
      if (type === 'image') {
        return entry.type === 'image';
      }
      return entry.type === 'video' || entry.type === 'playlist';
    });
  }

  private toPoolTypes(type: MediaOfflineType): Array<'image' | 'video'> {
    if (type === 'image') {
      return ['image'];
    }
    return ['video'];
  }

  private detectType(url: string, response: Response, typeHint: MediaOfflineType): MediaOfflineType {
    if (typeHint !== 'unknown') {
      return typeHint;
    }

    const lower = url.toLowerCase();
    if (lower.includes('.m3u8')) {
      return 'playlist';
    }
    if (/(\.ts|\.m4s|\.mp4|\.webm)(\?|$)/.test(lower)) {
      return 'video';
    }
    if (/(\.jpg|\.jpeg|\.png|\.webp|\.gif|\.avif)(\?|$)/.test(lower)) {
      return 'image';
    }

    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/vnd.apple.mpegurl')) {
      return 'playlist';
    }
    if (contentType.startsWith('video/')) {
      return 'video';
    }
    if (contentType.startsWith('image/')) {
      return 'image';
    }

    return 'unknown';
  }

  private getTtl(type: MediaOfflineType): number {
    switch (type) {
      case 'video':
        return this.videoTtlMs;
      case 'playlist':
        return this.playlistTtlMs;
      case 'image':
      default:
        return this.imageTtlMs;
    }
  }

  private async estimateResponseSize(response: Response): Promise<number> {
    const headerSize = Number(response.headers.get('content-length') || 0);
    if (headerSize > 0) {
      return headerSize;
    }

    if (response.type === 'opaque') {
      return 0;
    }

    try {
      const buffer = await response.clone().arrayBuffer();
      return buffer.byteLength;
    } catch {
      return 0;
    }
  }

  private persistIndex(): void {
    try {
      localStorage.setItem(this.indexStorageKey, JSON.stringify(this.index));
    } catch {
      // Ignore storage quota errors.
    }
  }

  private readIndex(): MediaOfflineIndex {
    try {
      const raw = localStorage.getItem(this.indexStorageKey);
      if (!raw) {
        return {version: this.indexVersion, entries: {}};
      }
      const parsed = JSON.parse(raw) as MediaOfflineIndex;
      if (!parsed?.entries || parsed.version !== this.indexVersion) {
        return {version: this.indexVersion, entries: {}};
      }
      return parsed;
    } catch {
      return {version: this.indexVersion, entries: {}};
    }
  }

  private isBrowser(): boolean {
    return typeof window !== 'undefined' && typeof caches !== 'undefined';
  }
}
